// src/pages/grid/GridSetupWizard.tsx
//
// A single guided flow for building a grid from scratch, so a coordinator never
// has to know which of the six grid pages to visit or in what order. It
// orchestrates the EXISTING functions — it introduces no new data model:
//   1. Basics   → useMultiGrid().createGrid(...)      (inserts a new grid)
//   2. Classes  → updateGrid(gridId, { classes })     + saveLineAvailability(...)
//   3. Finish   → hand off to the Timetable Editor to allocate teachers
//
// Every existing page (Import Grid, Classes, Line & Subject Setup, Line
// Scheduling, Timetable Editor) still works exactly as before; this is an
// additional, clearer front door. No existing grid or its data is ever touched —
// the wizard only creates a new grid and writes to that new grid.

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckIcon,
  UploadIcon,
  DownloadIcon,
  ArrowRightIcon,
  LayoutGridIcon,
  BookOpenIcon,
  FileTextIcon,
  AlertTriangleIcon,
} from 'lucide-react';
import PageHeader from '../../components/layout/PageHeader';
import Button from '../../components/ui/Button';
import { useToast } from '../../contexts/ToastContext';
import { useMultiGrid } from '../../contexts/MultiGridContext';
import { StaffAllocationGrid, LineDefinition } from '../../types/grid';
import { parseCsvFile, ParsedSubjectList, SubjectData } from '../../utils/csvParser';
import {
  buildClassesFromSubjects,
  buildLineSubjectMappings,
  saveLineAvailability,
} from '../../utils/gridSetup';

type StepId = 1 | 2 | 3;

const DEFAULT_LINES = ['A', 'B', 'C', 'D', 'E', 'F'];

// A lightweight local record of the grid we just created, so step 2 doesn't have
// to wait for the realtime context refresh to pick up the new row.
interface CreatedGrid {
  id: string;
  name: string;
  defaultClassMinutes: number;
  lines: LineDefinition[];
}

const STEPS: { id: StepId; label: string }[] = [
  { id: 1, label: 'Basics' },
  { id: 2, label: 'Classes' },
  { id: 3, label: 'Finish' },
];

const GridSetupWizard: React.FC = () => {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { gridTypes, createGrid, updateGrid } = useMultiGrid();

  const [step, setStep] = useState<StepId>(1);

  // Step 1 — basics
  const [gridName, setGridName] = useState('');
  const [selectedGridTypeId, setSelectedGridTypeId] = useState<string>('');
  const [creating, setCreating] = useState(false);
  const [created, setCreated] = useState<CreatedGrid | null>(null);

  // Step 2 — classes
  const [fileName, setFileName] = useState('');
  const [subjects, setSubjects] = useState<SubjectData[] | null>(null);
  const [parseError, setParseError] = useState<string | null>(null);
  const [savingClasses, setSavingClasses] = useState(false);
  const [classesAdded, setClassesAdded] = useState(0);
  const [availabilityAdded, setAvailabilityAdded] = useState(0);

  const selectedGridType = useMemo(
    () => gridTypes.find((t) => t.id === selectedGridTypeId) || null,
    [gridTypes, selectedGridTypeId]
  );

  // Lines that the chosen template will create (for the preview on step 1).
  const previewLines = useMemo(() => {
    if (selectedGridType?.availableLines?.length) return selectedGridType.availableLines;
    return DEFAULT_LINES;
  }, [selectedGridType]);

  const subjectsWithLine = useMemo(
    () => (subjects ? subjects.filter((s) => s.line && s.line.trim()).length : 0),
    [subjects]
  );

  // -- Step 1: create the grid ---------------------------------------------
  const handleCreateGrid = async () => {
    if (!gridName.trim()) {
      addToast('Give the grid a name to continue.', 'warning');
      return;
    }
    setCreating(true);
    try {
      const lines = previewLines;
      const minutes = selectedGridType?.defaultClassMinutes || 70;
      const midpoint = Math.ceil(lines.length / 2);
      const studyPeriods = [
        { id: 'semester1', name: 'Semester 1', lines: lines.slice(0, midpoint) },
        { id: 'semester2', name: 'Semester 2', lines: lines.slice(midpoint) },
      ];

      const gridId = await createGrid(gridName.trim(), minutes, lines, studyPeriods);

      const lineDefs: LineDefinition[] = lines.map((id) => ({
        id,
        name: `Line ${id}`,
        studyPeriod: studyPeriods.find((p) => p.lines.includes(id))?.id || 'semester1',
      }));

      setCreated({ id: gridId, name: gridName.trim(), defaultClassMinutes: minutes, lines: lineDefs });
      addToast(`Created grid "${gridName.trim()}" with ${lines.length} lines.`, 'success');
      setStep(2);
    } catch (error) {
      addToast(
        `Could not create the grid: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'error'
      );
    } finally {
      setCreating(false);
    }
  };

  // -- Step 2: parse + add classes -----------------------------------------
  const handleFile = async (file: File | undefined) => {
    if (!file) return;
    setParseError(null);
    setSubjects(null);
    setFileName(file.name);
    try {
      const parsed = await parseCsvFile(file);
      if (!('totalSubjects' in parsed)) {
        setParseError(
          'This looks like a pre-filled grid (matrix) file. For setup, upload a subject list with columns: Code, Name, Faculty, and optionally Line.'
        );
        return;
      }
      const list = parsed as ParsedSubjectList;
      const valid = list.subjects.filter((s) => s.name?.trim() && s.code?.trim());
      if (valid.length === 0) {
        setParseError('No valid rows found. Each row needs at least a Code and a Name.');
        return;
      }
      setSubjects(valid);
    } catch (error) {
      setParseError(
        `Could not read that file: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  };

  const handleAddClasses = async () => {
    if (!created || !subjects) return;
    setSavingClasses(true);
    try {
      const now = Date.now();
      const newClasses = buildClassesFromSubjects(subjects, created, now);
      await updateGrid(created.id, { classes: newClasses });

      let availabilityCount = 0;
      if (subjectsWithLine > 0) {
        const gridForMapping: StaffAllocationGrid = {
          id: created.id,
          name: created.name,
          defaultClassMinutes: created.defaultClassMinutes,
          lines: created.lines,
          classes: [],
          curriculumAreas: [],
          semesters: [],
        } as StaffAllocationGrid;
        const mappings = buildLineSubjectMappings(subjects, gridForMapping);
        if (mappings.length > 0) {
          await saveLineAvailability(created.id, mappings);
          availabilityCount = mappings.length;
        }
      }

      setClassesAdded(newClasses.length);
      setAvailabilityAdded(availabilityCount);
      addToast(
        availabilityCount > 0
          ? `Added ${newClasses.length} classes and ${availabilityCount} line-availability rules.`
          : `Added ${newClasses.length} classes.`,
        'success'
      );
      setStep(3);
    } catch (error) {
      addToast(
        `Could not add the classes: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'error'
      );
    } finally {
      setSavingClasses(false);
    }
  };

  const handleSkipClasses = () => {
    setClassesAdded(0);
    setAvailabilityAdded(0);
    setStep(3);
  };

  const downloadTemplate = () => {
    const csv =
      'Code,Name,Faculty,Line\n' +
      '12ENGSTD,English Standard,English,A\n' +
      '11MATMET,Mathematical Methods,Mathematics,B\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'grid-classes-template.csv';
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-3xl mx-auto">
      <PageHeader
        title="Build a Grid"
        description="Create a timetable grid, add its classes, and you're ready to allocate teachers — in three steps."
        helpText="This sets up a brand-new grid. Your existing grids are not affected. You can still use Import Grid, Classes and Line & Subject Setup separately at any time."
      />

      {/* Stepper */}
      <ol className="flex items-center gap-2 mb-8" aria-label="Progress">
        {STEPS.map((s, i) => {
          const state = step === s.id ? 'current' : step > s.id ? 'done' : 'todo';
          return (
            <React.Fragment key={s.id}>
              <li className="flex items-center gap-2">
                <span
                  className={[
                    'inline-flex h-7 w-7 items-center justify-center rounded-full text-body-sm font-semibold font-mono tabular-nums',
                    state === 'current' && 'bg-navy-900 text-white',
                    state === 'done' && 'bg-ok-solid text-white',
                    state === 'todo' && 'bg-ink-100 text-ink-400',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                >
                  {state === 'done' ? <CheckIcon className="h-4 w-4" /> : s.id}
                </span>
                <span
                  className={`text-body-sm font-semibold ${
                    state === 'todo' ? 'text-ink-400' : 'text-navy-900'
                  }`}
                >
                  {s.label}
                </span>
              </li>
              {i < STEPS.length - 1 && <li className="flex-1 h-px bg-ink-200" aria-hidden="true" />}
            </React.Fragment>
          );
        })}
      </ol>

      {/* Step 1 — Basics */}
      {step === 1 && (
        <div className="bg-white border border-ink-200 rounded-lg shadow-e1 p-6 space-y-5">
          <div className="flex items-center gap-2 text-navy-900">
            <LayoutGridIcon className="h-5 w-5" />
            <h2 className="text-card-title font-semibold">Name your grid</h2>
          </div>

          <div>
            <label htmlFor="grid-name" className="block text-body-sm font-semibold text-ink-700 mb-1.5">
              Grid name
            </label>
            <input
              id="grid-name"
              type="text"
              value={gridName}
              onChange={(e) => setGridName(e.target.value)}
              placeholder="e.g. 2027 Main Grid"
              className="w-full h-[38px] px-3 rounded-md border border-ink-300 text-body focus:outline-none focus:ring-2 focus:ring-navy-600/40"
            />
          </div>

          <div>
            <label htmlFor="grid-type" className="block text-body-sm font-semibold text-ink-700 mb-1.5">
              Template
              <span className="ml-1 font-normal text-ink-400">
                (sets the lines and class length)
              </span>
            </label>
            <select
              id="grid-type"
              value={selectedGridTypeId}
              onChange={(e) => setSelectedGridTypeId(e.target.value)}
              className="w-full h-[38px] px-3 rounded-md border border-ink-300 text-body bg-white focus:outline-none focus:ring-2 focus:ring-navy-600/40"
            >
              <option value="">Standard (lines A–F, 70 minutes)</option>
              {gridTypes.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name} — {(t.availableLines || []).length} lines, {t.defaultClassMinutes || 70} min
                </option>
              ))}
            </select>
          </div>

          <div className="rounded-md bg-ink-50 border border-ink-200 p-3">
            <p className="text-meta text-ink-400 mb-1">This grid will have</p>
            <p className="text-body-sm text-ink-700">
              <span className="font-mono tabular-nums">{previewLines.length}</span> lines:{' '}
              <span className="font-mono">{previewLines.join(', ')}</span>
            </p>
          </div>

          <div className="flex justify-end pt-1">
            <Button
              onClick={handleCreateGrid}
              isLoading={creating}
              disabled={!gridName.trim()}
              rightIcon={<ArrowRightIcon className="h-4 w-4" />}
            >
              Create grid & continue
            </Button>
          </div>
        </div>
      )}

      {/* Step 2 — Classes */}
      {step === 2 && created && (
        <div className="bg-white border border-ink-200 rounded-lg shadow-e1 p-6 space-y-5">
          <div className="flex items-center gap-2 text-navy-900">
            <BookOpenIcon className="h-5 w-5" />
            <h2 className="text-card-title font-semibold">Add your classes</h2>
          </div>

          <p className="text-body-sm text-ink-500">
            Upload a class list as a CSV with columns <strong>Code</strong>, <strong>Name</strong>,{' '}
            <strong>Faculty</strong>, and optionally <strong>Line</strong>. If you include a Line
            column, we'll also set which line each class is allowed to run on — no separate step
            needed.
          </p>

          <div className="flex flex-wrap items-center gap-3">
            <label className="inline-flex items-center gap-2 h-[38px] px-4 rounded-md bg-navy-900 text-white text-body font-semibold cursor-pointer hover:bg-navy-800">
              <UploadIcon className="h-4 w-4" />
              Choose CSV file
              <input
                type="file"
                accept=".csv"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
            </label>
            <button
              onClick={downloadTemplate}
              className="inline-flex items-center gap-1.5 text-body-sm font-semibold text-navy-600 hover:text-navy-700"
            >
              <DownloadIcon className="h-4 w-4" />
              Download template
            </button>
            {fileName && (
              <span className="inline-flex items-center gap-1.5 text-body-sm text-ink-500">
                <FileTextIcon className="h-4 w-4 text-ink-400" />
                {fileName}
              </span>
            )}
          </div>

          {parseError && (
            <div className="flex gap-2.5 p-3 rounded-md bg-warn-bg border border-warn-border">
              <AlertTriangleIcon className="h-[18px] w-[18px] text-warn-solid flex-shrink-0 mt-0.5" />
              <p className="text-body-sm text-warn-fg">{parseError}</p>
            </div>
          )}

          {subjects && (
            <div className="rounded-md bg-ok-bg border border-ok-border p-4">
              <p className="text-body-sm text-ok-fg">
                Ready to add <span className="font-mono tabular-nums font-semibold">{subjects.length}</span>{' '}
                classes
                {subjectsWithLine > 0 && (
                  <>
                    , <span className="font-mono tabular-nums font-semibold">{subjectsWithLine}</span> with
                    a line assignment
                  </>
                )}
                .
              </p>
            </div>
          )}

          <div className="flex items-center justify-between pt-1">
            <button
              onClick={handleSkipClasses}
              className="text-body-sm font-semibold text-ink-400 hover:text-ink-600"
            >
              Skip — I'll add classes later
            </button>
            <Button
              onClick={handleAddClasses}
              isLoading={savingClasses}
              disabled={!subjects}
              rightIcon={<ArrowRightIcon className="h-4 w-4" />}
            >
              Add classes & continue
            </Button>
          </div>
        </div>
      )}

      {/* Step 3 — Finish */}
      {step === 3 && created && (
        <div className="bg-white border border-ink-200 rounded-lg shadow-e1 p-6 space-y-5">
          <div className="flex items-center gap-2 text-ok-fg">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-ok-solid text-white">
              <CheckIcon className="h-5 w-5" />
            </span>
            <h2 className="text-card-title font-semibold text-navy-900">
              "{created.name}" is ready
            </h2>
          </div>

          <dl className="grid grid-cols-3 gap-3">
            <div className="rounded-md bg-ink-50 border border-ink-200 p-3">
              <dt className="text-meta text-ink-400">Lines</dt>
              <dd className="text-page-title font-mono tabular-nums text-navy-900">
                {created.lines.length}
              </dd>
            </div>
            <div className="rounded-md bg-ink-50 border border-ink-200 p-3">
              <dt className="text-meta text-ink-400">Classes</dt>
              <dd className="text-page-title font-mono tabular-nums text-navy-900">{classesAdded}</dd>
            </div>
            <div className="rounded-md bg-ink-50 border border-ink-200 p-3">
              <dt className="text-meta text-ink-400">Line rules</dt>
              <dd className="text-page-title font-mono tabular-nums text-navy-900">
                {availabilityAdded}
              </dd>
            </div>
          </dl>

          <div className="rounded-md bg-info-bg border border-info-border p-4">
            <p className="text-body-sm text-info-fg">
              <strong>Next:</strong> open the Timetable Editor to allocate teachers to classes.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3 pt-1">
            <Button
              onClick={() => navigate('/grid/multi-editor')}
              rightIcon={<ArrowRightIcon className="h-4 w-4" />}
            >
              Open Timetable Editor
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default GridSetupWizard;
